import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    this.hidden = false;
    this.removeAttribute('hidden');

    const matchingProductPrice = this.#findMatchingProductPrice(event.detail.data.html);
    const newPrice = matchingProductPrice?.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;
    if (!newPrice.textContent?.trim() && currentPrice.textContent?.trim()) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }
  };

  /**
   * Finds the matching product-price element in fetched HTML.
   * @param {Document} html - The parsed HTML document from the variant update response.
   * @returns {Element | null}
   */
  #findMatchingProductPrice(html) {
    const productId = this.dataset.productId;
    if (this.classList.contains('product-price-wrapper')) {
      const wrapperSelector = productId
        ? `product-price.product-price-wrapper[data-product-id="${productId}"]`
        : 'product-price.product-price-wrapper';
      return html.querySelector(wrapperSelector) ?? html.querySelector('product-price.product-price-wrapper');
    }

    if (!productId) return html.querySelector('product-price');

    const productPrices = html.querySelectorAll(`product-price[data-product-id="${productId}"]`);
    if (productPrices.length <= 1) return productPrices[0] ?? html.querySelector('product-price');

    const showDiscount = this.dataset.showDiscount ?? '';
    for (const productPrice of productPrices) {
      if ((productPrice.dataset.showDiscount ?? '') === showDiscount) {
        return productPrice;
      }
    }

    return productPrices[0];
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
